import { Injectable } from '@angular/core';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLActionClient, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { OutboundActionOutcome } from './outbound-action.client';

// ─────────────────────────────────────────────────────────────────────────────
// Conduit — the AIDP→Reply outbound UX. Typed client over the Conduit actions
// (List Sequence Bindings, Preview View Audience, Save Contact View) plus the
// pre-existing Reconcile / Sync Activities / Integration Health actions.
//
// Same contract as the other Outbound clients: invoke MJ Actions via
// GraphQLActionClient.RunAction; RunAction's ResultData is a JSON string, so
// every output is validated with hand-rolled guards before it reaches a screen.
// No dependency on the server-side Marketing package.
// ─────────────────────────────────────────────────────────────────────────────

/** One binding row from 'Outbound: List Sequence Bindings'. Read-only projection. */
export interface ConduitBinding {
    id: string;
    companyId: string | null;
    company: string | null;
    name: string | null;
    viewId: string | null;
    view: string | null;
    externalSequenceId: number | null;
    externalSequenceName: string | null;
    /** 'DryRun' | 'Live' — DryRun vs Live is a field ON the binding. */
    mode: string | null;
    removalMode: string | null;
    syncFrequencyHours: number | null;
    isActive: boolean;
    lastSyncAt: string | null;
    lastSuccessfulSyncAt: string | null;
    nextSyncAt: string | null;
    lastSyncStatus: string | null;
    lastSyncAddedCount: number | null;
    lastSyncRemovedCount: number | null;
    lastSyncErrorCount: number | null;
    lastSyncMessage: string | null;
}

/** One sample contact from 'Outbound: Preview View Audience'. */
export interface ConduitSampleContact {
    id: string;
    name: string;
    org: string;
    title: string;
    email: string;
}

/** Result of 'Outbound: Preview View Audience'. */
export interface ConduitAudiencePreview {
    totalCount: number;
    sample: ConduitSampleContact[];
    duplicateEmailCount: number;
    noEmailCount: number;
    generatedWhereClause: string;
}

/** Result of 'Outbound: Save Contact View'. */
export interface ConduitSaveViewResult {
    viewId: string;
    generatedWhereClause: string;
    explanation: string;
}

/** One Reply sequence option for the Bind-to-sequence picker. */
export interface ConduitReplySequence {
    id: number;
    name: string;
    status: string;
}

/** One saved contact-view option from 'Outbound: List Contact Views'. */
export interface ConduitContactView {
    viewId: string;
    name: string;
    whereClause: string;
    smartFilterWhereClause: string;
    updatedAt: string | null;
}

/** One "will be added" row in the Dry Run per-contact diff. */
export interface ConduitDiffAddRow {
    email: string;
    reason: string;
}

/** One "will be removed" row in the Dry Run per-contact diff. */
export interface ConduitDiffRemoveRow {
    email: string;
    reason: string;
    statusInSequence: string | null;
}

/** Result of 'Outbound: Reconcile Sequence Binding' (DryRun reads counts + per-contact diff). */
export interface ConduitReconcileResult {
    addedCount: number;
    removedCount: number;
    errorCount: number;
    lastSyncStatus: string;
    wasDryRun: boolean;
    integration: string;
    /** The server Message — carries the per-binding human-readable summary. */
    message: string;
    /** DryRun only: bounded sample (max 100) of who would be added, with reasons. */
    addSample: ConduitDiffAddRow[];
    /** True when AddedCount exceeds the add-sample cap. */
    addSampleTruncated: boolean;
    /** DryRun only: bounded sample (max 100) of who would be removed, with reasons + status. */
    removeSample: ConduitDiffRemoveRow[];
    /** True when RemovedCount exceeds the remove-sample cap. */
    removeSampleTruncated: boolean;
}

/** Result of 'Outbound: Create Sequence Binding'. */
export interface ConduitCreateBindingResult {
    bindingId: string;
    mode: string;
}

/** Result of 'Outbound: Sync Reply Activities'. */
export interface ConduitSyncActivitiesResult {
    companiesProcessed: number;
    eventsFetched: number;
    activitiesCreated: number;
    activitiesSkipped: number;
    errorCount: number;
    wasDryRun: boolean;
    summary: string;
}

/** One resulting Activity row surfaced on the Replies→Activities screen. */
export interface ConduitActivityRow {
    id: string;
    type: string;
    title: string;
    description: string;
    activityDate: string | null;
    isReplied: boolean;
    isBounced: boolean;
}

/** One per-integration health entry from the HealthReport. */
export interface ConduitIntegrationHealth {
    integration: string;
    company: string;
    status: string;
    lastRunAt: string | null;
    finished: boolean;
    errored: boolean;
    ageHours: number | null;
    isStale: boolean;
    totalRecords: number | null;
}

/** Result of 'Integration: Get Integration Health'. */
export interface ConduitHealthReport {
    integrations: ConduitIntegrationHealth[];
    integrationCount: number;
    staleCount: number;
    erroredCount: number;
    checkedAt: string | null;
    thresholdHours: number | null;
}

// ─── guards (RunAction.Result is a JSON string → validate before use) ───────

function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function strOr(v: unknown, fallback: string): string {
    return typeof v === 'string' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
    return typeof v === 'string' && v.length > 0 ? v : null;
}
function numOr0(v: unknown): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}
function numOrNull(v: unknown): number | null {
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUuid(v: string): boolean {
    return UUID_RE.test(v);
}

/**
 * Parse an output param the server set to a JSON *string* (Bindings / Sample /
 * HealthReport are JSON.stringify'd). The transport may hand it back as the raw
 * string OR already-parsed; accept both. Returns `unknown` for guard-based
 * narrowing (no `as` on parse output). Logs + returns null on malformed JSON.
 */
function parseJsonOutput(v: unknown, context: string): unknown {
    if (typeof v !== 'string') return v;
    if (v.trim().length === 0) return null;
    try {
        return JSON.parse(v);
    } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        LogError(`[ConduitActionClient] ${context}: malformed JSON output: ${msg}`);
        return null;
    }
}

function toBindings(v: unknown): ConduitBinding[] {
    const parsed = parseJsonOutput(v, 'listSequenceBindings.Bindings');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecord).map(r => ({
        id: strOr(r['ID'], ''),
        companyId: strOrNull(r['CompanyID']),
        company: strOrNull(r['Company']),
        name: strOrNull(r['Name']),
        viewId: strOrNull(r['ViewID']),
        view: strOrNull(r['View']),
        externalSequenceId: numOrNull(r['ExternalSequenceID']),
        externalSequenceName: strOrNull(r['ExternalSequenceName']),
        mode: strOrNull(r['Mode']),
        removalMode: strOrNull(r['RemovalMode']),
        syncFrequencyHours: numOrNull(r['SyncFrequencyHours']),
        isActive: r['IsActive'] === true || r['IsActive'] === 1,
        lastSyncAt: strOrNull(r['LastSyncAt']),
        lastSuccessfulSyncAt: strOrNull(r['LastSuccessfulSyncAt']),
        nextSyncAt: strOrNull(r['NextSyncAt']),
        lastSyncStatus: strOrNull(r['LastSyncStatus']),
        lastSyncAddedCount: numOrNull(r['LastSyncAddedCount']),
        lastSyncRemovedCount: numOrNull(r['LastSyncRemovedCount']),
        lastSyncErrorCount: numOrNull(r['LastSyncErrorCount']),
        lastSyncMessage: strOrNull(r['LastSyncMessage']),
    })).filter(b => b.id.length > 0);
}

function toSample(v: unknown): ConduitSampleContact[] {
    const parsed = parseJsonOutput(v, 'previewViewAudience.Sample');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecord).map(r => ({
        id: strOr(r['id'], ''),
        name: strOr(r['name'], ''),
        org: strOr(r['org'], ''),
        title: strOr(r['title'], ''),
        email: strOr(r['email'], ''),
    }));
}

function toDiffAddRows(v: unknown): ConduitDiffAddRow[] {
    const parsed = parseJsonOutput(v, 'reconcileBinding.AddSample');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecord).map(r => ({
        email: strOr(r['email'], ''),
        reason: strOr(r['reason'], ''),
    })).filter(r => r.email.length > 0);
}

function toDiffRemoveRows(v: unknown): ConduitDiffRemoveRow[] {
    const parsed = parseJsonOutput(v, 'reconcileBinding.RemoveSample');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecord).map(r => ({
        email: strOr(r['email'], ''),
        reason: strOr(r['reason'], ''),
        statusInSequence: strOrNull(r['statusInSequence']),
    })).filter(r => r.email.length > 0);
}

function toContactViews(v: unknown): ConduitContactView[] {
    const parsed = parseJsonOutput(v, 'listContactViews.Views');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecord).map(r => ({
        viewId: strOr(r['ViewID'], ''),
        name: strOr(r['Name'], ''),
        whereClause: strOr(r['WhereClause'], ''),
        smartFilterWhereClause: strOr(r['SmartFilterWhereClause'], ''),
        updatedAt: strOrNull(r['UpdatedAt']),
    })).filter(v => v.viewId.length > 0);
}

function toReplySequences(v: unknown): ConduitReplySequence[] {
    const parsed = parseJsonOutput(v, 'listReplySequences.Sequences');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecord).map(r => ({
        id: numOr0(r['id']),
        name: strOr(r['name'], ''),
        status: strOr(r['status'], ''),
    })).filter(s => s.id > 0);
}

function toIntegrationHealth(v: unknown): ConduitIntegrationHealth[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(r => ({
        integration: strOr(r['integration'], ''),
        company: strOr(r['company'], ''),
        status: strOr(r['status'], 'unknown'),
        lastRunAt: strOrNull(r['lastRunAt']),
        finished: r['finished'] === true,
        errored: r['errored'] === true,
        ageHours: numOrNull(r['ageHours']),
        isStale: r['isStale'] === true,
        totalRecords: numOrNull(r['totalRecords']),
    }));
}

interface RunActionCallResult {
    found: boolean;
    success: boolean;
    message: string;
    outputs: Map<string, unknown>;
}

/** Non-'Outbound:'-prefixed actions this client resolves by name. */
const EXTRA_ACTION_NAMES: readonly string[] = [
    'Integration: Get Integration Health',
    'Query Records',
];

function extractOutputs(result: unknown): Map<string, unknown> {
    const outputs = new Map<string, unknown>();
    const entries: unknown[] = Array.isArray(result)
        ? result
        : isRecord(result) ? Object.values(result) : [];
    for (const p of entries) {
        if (isRecord(p) && typeof p['Name'] === 'string') {
            outputs.set(p['Name'], p['Value']);
        }
    }
    return outputs;
}

/**
 * Production Conduit client. Invokes the Conduit + reuse actions via the
 * framework's GraphQLActionClient. NOTHING here mutates Reply except the
 * explicit Live reconcile path, which is gated behind the Dry Run → Approve UI.
 */
@Injectable()
export class ConduitActionClient {
    private readonly actionIdCache = new Map<string, string>();
    private actionIdMapLoaded = false;

    // ─── Bindings overview ──────────────────────────────────────────────────

    async listBindings(companyId?: string, activeOnly = false): Promise<OutboundActionOutcome<ConduitBinding[]>> {
        const inputs: { Name: string; Value: string }[] = [];
        if (companyId) {
            if (!isUuid(companyId)) return { kind: 'error', message: 'CompanyID must be a valid UUID' };
            inputs.push({ Name: 'CompanyID', Value: companyId });
        }
        if (activeOnly) inputs.push({ Name: 'ActiveOnly', Value: 'true' });
        const call = await this.runAction('Outbound: List Sequence Bindings', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return { kind: 'success', data: toBindings(call.outputs.get('Bindings')) };
    }

    // ─── Saved contact views (the Bind-screen picker + Audiences list) ──────

    /**
     * List saved MJ User Views over the Contacts entity for the Bind-to-Sequence
     * view picker and the Audiences tab. Read-only.
     */
    async listContactViews(includeShared = true): Promise<OutboundActionOutcome<ConduitContactView[]>> {
        const inputs: { Name: string; Value: string }[] = [];
        if (!includeShared) inputs.push({ Name: 'IncludeShared', Value: 'false' });
        const call = await this.runAction('Outbound: List Contact Views', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return { kind: 'success', data: toContactViews(call.outputs.get('Views')) };
    }

    // ─── Audience preview ───────────────────────────────────────────────────

    /** Preview a saved view (viewId) OR an unsaved prompt (smartFilterPrompt). */
    async previewAudience(opts: { viewId?: string; smartFilterPrompt?: string; whereClause?: string }): Promise<OutboundActionOutcome<ConduitAudiencePreview>> {
        const inputs: { Name: string; Value: string }[] = [];
        if (opts.viewId) {
            if (!isUuid(opts.viewId)) return { kind: 'error', message: 'ViewID must be a valid UUID' };
            inputs.push({ Name: 'ViewID', Value: opts.viewId });
        }
        const prompt = (opts.smartFilterPrompt ?? '').trim();
        if (prompt.length > 0) inputs.push({ Name: 'SmartFilterPrompt', Value: prompt });
        // An operator-edited clause to preview directly (read-only re-preview).
        const editedClause = (opts.whereClause ?? '').trim();
        if (editedClause.length > 0) inputs.push({ Name: 'WhereClause', Value: editedClause });
        if (inputs.length === 0) {
            return { kind: 'error', message: 'Provide a saved view, an audience description, or a WHERE clause to preview.' };
        }
        const call = await this.runAction('Outbound: Preview View Audience', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                totalCount: numOr0(call.outputs.get('TotalCount')),
                sample: toSample(call.outputs.get('Sample')),
                duplicateEmailCount: numOr0(call.outputs.get('DuplicateEmailCount')),
                noEmailCount: numOr0(call.outputs.get('NoEmailCount')),
                generatedWhereClause: strOr(call.outputs.get('GeneratedWhereClause'), ''),
            },
        };
    }

    // ─── Save Smart-Filter contact view ─────────────────────────────────────

    /**
     * Save (or update) a Smart-Filter contact view.
     *
     * When `whereClause` is provided (non-empty), it is the operator-edited /
     * verified clause from the Conduit verify pane — the action uses it verbatim
     * and skips AI generation. Omit it to let the action generate from the prompt.
     */
    async saveContactView(name: string, smartFilterPrompt: string, viewId?: string, whereClause?: string): Promise<OutboundActionOutcome<ConduitSaveViewResult>> {
        if (!name || name.trim().length === 0) return { kind: 'error', message: 'A view name is required.' };
        if (!smartFilterPrompt || smartFilterPrompt.trim().length === 0) {
            return { kind: 'error', message: 'An audience description is required.' };
        }
        const inputs: { Name: string; Value: string }[] = [
            { Name: 'Name', Value: name.trim() },
            { Name: 'SmartFilterPrompt', Value: smartFilterPrompt.trim() },
        ];
        if (viewId) {
            if (!isUuid(viewId)) return { kind: 'error', message: 'ViewID must be a valid UUID' };
            inputs.push({ Name: 'ViewID', Value: viewId });
        }
        const editedClause = (whereClause ?? '').trim();
        if (editedClause.length > 0) {
            inputs.push({ Name: 'WhereClause', Value: editedClause });
        }
        const call = await this.runAction('Outbound: Save Contact View', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                viewId: strOr(call.outputs.get('ViewID'), ''),
                generatedWhereClause: strOr(call.outputs.get('GeneratedWhereClause'), ''),
                explanation: strOr(call.outputs.get('Explanation'), ''),
            },
        };
    }

    // ─── Reconcile (dry-run reads counts; live applies) ─────────────────────

    /**
     * Reconcile one binding. DryRun vs Live is a field on the binding record —
     * NOT an input — so a dry-run preview requires the binding's Mode='DryRun'
     * and a live apply requires Mode='Live'. The UI flips that field via the
     * Approve gate before calling this.
     */
    async reconcileBinding(bindingId: string): Promise<OutboundActionOutcome<ConduitReconcileResult>> {
        if (!bindingId || !isUuid(bindingId)) return { kind: 'error', message: 'BindingID must be a valid UUID' };
        const call = await this.runAction('Outbound: Reconcile Sequence Binding', [
            { Name: 'BindingID', Value: bindingId },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        // Reconcile returns Success=false on EMPTY_VIEW_GUARD / Failed but still
        // carries outputs; surface the message either way for the UI to show.
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                addedCount: numOr0(call.outputs.get('AddedCount')),
                removedCount: numOr0(call.outputs.get('RemovedCount')),
                errorCount: numOr0(call.outputs.get('ErrorCount')),
                lastSyncStatus: strOr(call.outputs.get('LastSyncStatus'), 'unknown'),
                wasDryRun: call.outputs.get('WasDryRun') === true,
                integration: strOr(call.outputs.get('Integration'), ''),
                message: call.message,
                addSample: toDiffAddRows(call.outputs.get('AddSample')),
                addSampleTruncated: call.outputs.get('AddSampleTruncated') === true,
                removeSample: toDiffRemoveRows(call.outputs.get('RemoveSample')),
                removeSampleTruncated: call.outputs.get('RemoveSampleTruncated') === true,
            },
        };
    }

    // ─── Bind-to-sequence: list Reply sequences + create the binding ─────────

    /** Populate the Bind screen's Reply-sequence picker for a brand. */
    async listReplySequences(companyId: string): Promise<OutboundActionOutcome<ConduitReplySequence[]>> {
        if (!companyId || !isUuid(companyId)) return { kind: 'error', message: 'CompanyID must be a valid UUID' };
        const call = await this.runAction('Outbound: Get Reply Sequences', [
            { Name: 'CompanyID', Value: companyId },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return { kind: 'success', data: toReplySequences(call.outputs.get('Sequences')) };
    }

    /**
     * Create a sequence binding (the last step of the Bind flow). New bindings
     * are created in DryRun mode by the action, so the caller routes to the
     * binding's Dry Run next — nobody is enrolled until the Approve gate.
     */
    async createBinding(opts: {
        companyId: string;
        viewId: string;
        externalSequenceId: number;
        externalSequenceName?: string;
        name?: string;
        removalMode?: 'Finish' | 'Pause' | 'Remove';
        syncFrequencyHours?: number;
    }): Promise<OutboundActionOutcome<ConduitCreateBindingResult>> {
        if (!opts.companyId || !isUuid(opts.companyId)) return { kind: 'error', message: 'CompanyID must be a valid UUID' };
        if (!opts.viewId || !isUuid(opts.viewId)) return { kind: 'error', message: 'A saved view is required (ViewID must be a valid UUID).' };
        if (!Number.isInteger(opts.externalSequenceId) || opts.externalSequenceId < 1) {
            return { kind: 'error', message: 'Pick a Reply sequence to bind to.' };
        }
        const inputs: { Name: string; Value: string }[] = [
            { Name: 'CompanyID', Value: opts.companyId },
            { Name: 'ViewID', Value: opts.viewId },
            { Name: 'ExternalSequenceID', Value: String(opts.externalSequenceId) },
        ];
        if (opts.externalSequenceName) inputs.push({ Name: 'ExternalSequenceName', Value: opts.externalSequenceName });
        if (opts.name) inputs.push({ Name: 'Name', Value: opts.name });
        if (opts.removalMode) inputs.push({ Name: 'RemovalMode', Value: opts.removalMode });
        if (opts.syncFrequencyHours != null) inputs.push({ Name: 'SyncFrequencyHours', Value: String(opts.syncFrequencyHours) });
        // Mode is omitted — the action defaults new bindings to DryRun (safe).
        const call = await this.runAction('Outbound: Create Sequence Binding', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                bindingId: strOr(call.outputs.get('BindingID'), ''),
                mode: strOr(call.outputs.get('Mode'), 'DryRun'),
            },
        };
    }

    /**
     * Flip a binding's Mode (the Approve gate). Routed through the narrow,
     * operator-gated 'Outbound: Set Binding Mode' action — NOT a generic
     * Update Record — so the one field that decides whether the next reconcile
     * mutates Reply has a single audited chokepoint. The next reconcile then
     * applies to Reply when Mode='Live'.
     */
    async setBindingMode(bindingId: string, mode: 'DryRun' | 'Live'): Promise<{ success: boolean; message: string }> {
        if (!bindingId || !isUuid(bindingId)) return { success: false, message: 'BindingID must be a valid UUID' };
        const call = await this.runAction('Outbound: Set Binding Mode', [
            { Name: 'BindingID', Value: bindingId },
            { Name: 'Mode', Value: mode },
        ]);
        if (!call.found) return { success: false, message: `Set Binding Mode action not deployed: ${call.message}` };
        return { success: call.success, message: call.message };
    }

    // ─── Replies → Activities ───────────────────────────────────────────────

    async syncReplyActivities(companyId: string | undefined, dryRun: boolean): Promise<OutboundActionOutcome<ConduitSyncActivitiesResult>> {
        const inputs: { Name: string; Value: string }[] = [{ Name: 'DryRun', Value: String(dryRun) }];
        if (companyId) {
            if (!isUuid(companyId)) return { kind: 'error', message: 'CompanyID must be a valid UUID' };
            inputs.push({ Name: 'CompanyID', Value: companyId });
        }
        const call = await this.runAction('Outbound: Sync Reply Activities', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                companiesProcessed: numOr0(call.outputs.get('CompaniesProcessed')),
                eventsFetched: numOr0(call.outputs.get('EventsFetched')),
                activitiesCreated: numOr0(call.outputs.get('ActivitiesCreated')),
                activitiesSkipped: numOr0(call.outputs.get('ActivitiesSkipped')),
                errorCount: numOr0(call.outputs.get('ErrorCount')),
                wasDryRun: call.outputs.get('WasDryRun') === true,
                summary: strOr(call.outputs.get('Summary'), call.message),
            },
        };
    }

    /** List the resulting reply/bounce Activities (via generic 'Query Records'). */
    async listReplyActivities(companyIntegrationId?: string): Promise<OutboundActionOutcome<ConduitActivityRow[]>> {
        const filters = ["(IsReplied=1 OR IsBounced=1)"];
        if (companyIntegrationId) {
            if (!isUuid(companyIntegrationId)) return { kind: 'error', message: 'CompanyIntegrationID must be a valid UUID' };
            filters.push(`CompanyIntegrationID='${companyIntegrationId}'`);
        }
        const call = await this.runAction('Query Records', [
            { Name: 'EntityName', Value: 'Activities' },
            { Name: 'ExtraFilter', Value: filters.join(' AND ') },
            { Name: 'OrderBy', Value: 'ActivityDate DESC' },
            { Name: 'MaxRows', Value: '100' },
            { Name: 'ResultType', Value: 'simple' },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        const raw = call.outputs.get('Records');
        const rows: ConduitActivityRow[] = Array.isArray(raw)
            ? raw.filter(isRecord).map(r => ({
                id: strOr(r['ID'], ''),
                type: strOr(r['Type'], ''),
                title: strOr(r['Title'], ''),
                description: strOr(r['Description'], ''),
                activityDate: strOrNull(r['ActivityDate']),
                isReplied: r['IsReplied'] === true || r['IsReplied'] === 1,
                isBounced: r['IsBounced'] === true || r['IsBounced'] === 1,
            })).filter(a => a.id.length > 0)
            : [];
        return { kind: 'success', data: rows };
    }

    // ─── Integration health ─────────────────────────────────────────────────

    async getIntegrationHealth(stalenessThresholdHours?: number): Promise<OutboundActionOutcome<ConduitHealthReport>> {
        const inputs: { Name: string; Value: string }[] = [];
        if (stalenessThresholdHours && Number.isFinite(stalenessThresholdHours)) {
            inputs.push({ Name: 'StalenessThresholdHours', Value: String(Math.round(stalenessThresholdHours)) });
        }
        const call = await this.runAction('Integration: Get Integration Health', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        const report = parseJsonOutput(call.outputs.get('HealthReport'), 'getIntegrationHealth.HealthReport');
        const r = isRecord(report) ? report : {};
        return {
            kind: 'success',
            data: {
                integrations: toIntegrationHealth(r['integrations']),
                integrationCount: numOr0(call.outputs.get('IntegrationCount')),
                staleCount: numOr0(call.outputs.get('StaleCount')),
                erroredCount: numOr0(call.outputs.get('ErroredCount')),
                checkedAt: strOrNull(r['checkedAt']),
                thresholdHours: numOrNull(r['thresholdHours']),
            },
        };
    }

    // ─── transport (mirrors GraphQLOutboundActionClient) ────────────────────

    private async ensureActionIdMap(): Promise<void> {
        if (this.actionIdMapLoaded) return;
        const extraInClause = EXTRA_ACTION_NAMES.map(n => `'${n}'`).join(', ');
        const rv = new RunView();
        const result = await rv.RunView<{ ID: string; Name: string }>({
            EntityName: 'MJ: Actions',
            ExtraFilter: `Name LIKE 'Outbound: %' OR Name LIKE 'Integration: %' OR Name IN (${extraInClause})`,
            Fields: ['ID', 'Name'],
            ResultType: 'simple',
        });
        if (!result.Success) {
            throw new Error(result.ErrorMessage || 'Failed to load Conduit action metadata');
        }
        for (const row of result.Results ?? []) this.actionIdCache.set(row.Name, row.ID);
        this.actionIdMapLoaded = true;
    }

    private async runAction(actionName: string, params: { Name: string; Value: string }[]): Promise<RunActionCallResult> {
        try {
            await this.ensureActionIdMap();
            const actionId = this.actionIdCache.get(actionName);
            if (!actionId) {
                return {
                    found: false,
                    success: false,
                    message: `Action '${actionName}' is not registered in this environment's metadata`,
                    outputs: new Map<string, unknown>(),
                };
            }
            const client = new GraphQLActionClient(Metadata.Provider as GraphQLDataProvider);
            const result = await client.RunAction(
                actionId,
                params.map(p => ({ Name: p.Name, Value: p.Value, Type: 'Input' as const })),
            );
            const success = result.Success === true;
            return {
                found: true,
                success,
                message: result.Message || (success ? 'OK' : 'Unknown error'),
                outputs: extractOutputs(result.Result),
            };
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ConduitActionClient] '${actionName}' failed: ${msg}`);
            return { found: true, success: false, message: msg, outputs: new Map<string, unknown>() };
        }
    }
}
